<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { stageColor } from 'dashboard/components-next/Contacts/contactStageColors';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';

import Icon from 'dashboard/components-next/icon/Icon.vue';

// Filtros sobre la lista de la bandeja: un desplegable por cada atributo de
// contacto sincronizado con etiquetas (Tipo, Estado y los que añada la
// cuenta), precedidos de Canal sólo si el agente tiene más de una bandeja.
// Los valores se envían a la API, que filtra por los atributos del contacto.
const props = defineProps({
  contactAttributes: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['changeContactAttributes']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

// Con muchos atributos la fila crecería demasiado en un panel de 366px, así
// que sólo se ven los primeros y el resto se despliega bajo demanda. Los
// que están filtrando se muestran siempre.
const MAX_VISIBLE = 3;

const NEUTRAL_DOT = '#b8c5cf';
const TRIGGER_CLASS =
  'inline-flex items-center gap-[7px] h-7 px-2.5 rounded-[7px] text-[11.5px] whitespace-nowrap max-w-[9.5rem] transition-colors';
const PANEL_CLASS =
  'absolute z-30 top-full mt-1.5 ltr:left-0 rtl:right-0 w-[238px] pt-1 pb-1.5 rounded-[10px] border border-n-weak bg-n-solid-2 shadow-[0_10px_28px_rgba(20,40,60,.15)] max-h-72 overflow-y-auto';
const PANEL_TITLE_CLASS =
  'px-3 pt-2 pb-1.5 text-[10.5px] font-semibold tracking-[0.1em] uppercase text-n-slate-10';
const ROW_CLASS =
  'flex items-center gap-[9px] w-full h-[31px] px-3 text-[12.5px] text-left';

const inboxes = useMapGetter('inboxes/getInboxes');
const contactAttributeDefinitions = useMapGetter(
  'attributes/getContactAttributes'
);

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);
const showChannelFilter = computed(() => sortedInboxes.value.length > 1);

const openMenu = ref(null);
const showAll = ref(false);
const toggleMenu = key => {
  openMenu.value = openMenu.value === key ? null : key;
};
const closeMenu = () => {
  openMenu.value = null;
};

// ---- Canal -------------------------------------------------------------------
const currentChannelId = computed(() => {
  if (['inbox_dashboard', 'conversation_through_inbox'].includes(route.name)) {
    return Number(route.params.inbox_id);
  }
  return null;
});
const channelLabel = computed(
  () =>
    sortedInboxes.value.find(inbox => inbox.id === currentChannelId.value)
      ?.name || t('CHAT_LIST.SCOPE_FILTER.CHANNEL')
);
const goToChannel = inboxId => {
  closeMenu();
  if (!inboxId) {
    router.push(accountScopedRoute('home'));
    return;
  }
  router.push(accountScopedRoute('inbox_dashboard', { inbox_id: inboxId }));
};

// ---- Atributos sincronizados --------------------------------------------------
const syncedAttributes = computed(() =>
  syncedListAttributes(contactAttributeDefinitions.value)
);

const valueFor = attribute =>
  props.contactAttributes?.[attribute.attributeKey] || null;

const menus = computed(() =>
  syncedAttributes.value.map(attribute => ({
    key: attribute.attributeKey,
    attribute,
    current: valueFor(attribute),
    placeholder: attribute.attributeDisplayName,
  }))
);

// Un filtro activo nunca se esconde: si no, se estaría filtrando sin verlo.
const visibleMenus = computed(() => {
  if (showAll.value) return menus.value;
  const budget = MAX_VISIBLE - (showChannelFilter.value ? 1 : 0);
  return menus.value.filter(
    (menu, index) => index < budget || Boolean(menu.current)
  );
});
const hiddenCount = computed(
  () => menus.value.length - visibleMenus.value.length
);

const setAttributeValue = (attribute, value) => {
  closeMenu();
  const next = { ...(props.contactAttributes || {}) };
  if (value) {
    next[attribute.attributeKey] = value;
  } else {
    delete next[attribute.attributeKey];
  }
  emit('changeContactAttributes', next);
};

const triggerClass = active =>
  active
    ? 'bg-n-solid-blue text-n-blue-text font-semibold'
    : 'bg-n-alpha-2 text-n-slate-11 font-medium hover:text-n-slate-12';
const rowClass = active =>
  active
    ? 'bg-n-blue-2 text-n-blue-text font-semibold'
    : 'text-n-slate-11 font-medium hover:bg-n-alpha-1';
const dotStyle = value => ({
  backgroundColor: value ? stageColor(value).dot : NEUTRAL_DOT,
});

onMounted(() => {
  if (!inboxes.value?.length) store.dispatch('inboxes/get');
  if (!contactAttributeDefinitions.value?.length) {
    store.dispatch('attributes/get');
  }
});
</script>

<template>
  <div class="flex flex-wrap items-center gap-1.5">
    <!-- Canal (sólo con más de una bandeja) -->
    <div v-if="showChannelFilter" class="relative">
      <button
        type="button"
        :class="[TRIGGER_CLASS, triggerClass(currentChannelId)]"
        @click="toggleMenu('channel')"
      >
        <span class="truncate">{{ channelLabel }}</span>
        <Icon
          icon="i-lucide-chevron-down"
          size="size-[11px] flex-shrink-0 opacity-55"
        />
      </button>
      <div
        v-if="openMenu === 'channel'"
        v-on-clickaway="closeMenu"
        :class="PANEL_CLASS"
      >
        <div :class="PANEL_TITLE_CLASS">
          {{ t('CHAT_LIST.SCOPE_FILTER.CHANNEL') }}
        </div>
        <button
          type="button"
          :class="[ROW_CLASS, rowClass(!currentChannelId)]"
          @click="goToChannel(null)"
        >
          <span class="flex-1 truncate">
            {{ t('CHAT_LIST.SCOPE_FILTER.ALL') }}
          </span>
          <Icon
            v-if="!currentChannelId"
            icon="i-lucide-check"
            size="size-3 text-n-blue-text"
          />
        </button>
        <button
          v-for="inbox in sortedInboxes"
          :key="inbox.id"
          type="button"
          :class="[ROW_CLASS, rowClass(currentChannelId === inbox.id)]"
          @click="goToChannel(inbox.id)"
        >
          <span class="flex-1 truncate">{{ inbox.name }}</span>
          <Icon
            v-if="currentChannelId === inbox.id"
            icon="i-lucide-check"
            size="size-3 text-n-blue-text"
          />
        </button>
      </div>
    </div>

    <!-- Un filtro por atributo sincronizado con etiquetas -->
    <div v-for="menu in visibleMenus" :key="menu.key" class="relative">
      <button
        type="button"
        :class="[TRIGGER_CLASS, triggerClass(menu.current)]"
        @click="toggleMenu(menu.key)"
      >
        <span
          class="size-[7px] rounded-full flex-shrink-0"
          :style="dotStyle(menu.current)"
        />
        <span class="truncate">{{ menu.current || menu.placeholder }}</span>
        <Icon
          icon="i-lucide-chevron-down"
          size="size-[11px] flex-shrink-0 opacity-55"
        />
      </button>
      <div
        v-if="openMenu === menu.key"
        v-on-clickaway="closeMenu"
        :class="PANEL_CLASS"
      >
        <div :class="PANEL_TITLE_CLASS">
          {{ menu.attribute.attributeDisplayName }}
        </div>
        <button
          type="button"
          :class="[ROW_CLASS, rowClass(!menu.current)]"
          @click="setAttributeValue(menu.attribute, null)"
        >
          <span
            class="size-[9px] rounded-full flex-shrink-0"
            :style="dotStyle(null)"
          />
          <span class="flex-1 truncate">
            {{ t('CHAT_LIST.SCOPE_FILTER.ALL') }}
          </span>
          <Icon
            v-if="!menu.current"
            icon="i-lucide-check"
            size="size-3 text-n-blue-text"
          />
        </button>
        <button
          v-for="value in menu.attribute.attributeValues || []"
          :key="value"
          type="button"
          :class="[ROW_CLASS, rowClass(menu.current === value)]"
          @click="setAttributeValue(menu.attribute, value)"
        >
          <span
            class="size-[9px] rounded-full flex-shrink-0"
            :style="dotStyle(value)"
          />
          <span class="flex-1 truncate">{{ value }}</span>
          <Icon
            v-if="menu.current === value"
            icon="i-lucide-check"
            size="size-3 text-n-blue-text"
          />
        </button>
      </div>
    </div>

    <button
      v-if="hiddenCount > 0"
      type="button"
      class="inline-flex items-center h-7 px-2.5 rounded-[7px] bg-n-alpha-2 text-[11.5px] font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
      @click="showAll = true"
    >
      {{ t('CHAT_LIST.SCOPE_FILTER.MORE_FILTERS', { count: hiddenCount }) }}
    </button>
  </div>
</template>
